import React, { useEffect } from "react";
import { Chip } from "react-native-paper";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import { PickerFieldWrapper } from "./PickerFieldWrapper";
import { RootStackParamList } from "../../types/navigationParams";
import { Field } from "../../config/companies/config";

interface DropdownFieldProps {
  field: Field;
  value: any;
  onChange: (fieldName: string, newValue: any) => void;
  errors?: { [key: string]: string };
  formData?: { [key: string]: any };
}

export const DropdownField = ({
  field,
  value,
  onChange,
  errors = {},
  formData,
}: DropdownFieldProps) => {
  const { t } = useTranslation();
  const navigate = useNavigation<NavigationProp<RootStackParamList>>();
  const getPathValue = (obj: Record<string, any> | undefined, path: string) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc: any, key: string) => acc?.[key], obj);
  };

  const sourceValue = field.autoSelectFrom
    ? getPathValue(formData, field.autoSelectFrom)
    : undefined;

  const hasValue = Array.isArray(value)
    ? value.length > 0
    : value !== undefined && value !== null && value !== "";

  // Auto-select based on another field's value
  useEffect(() => {
    if (field.autoSelectFrom && sourceValue && field.options && !hasValue) {
      if (sourceValue) {
        const sourceText = String(sourceValue).toLowerCase();

        // Find matching option in source text
        const matchedOption = field.options.find((option) =>
          sourceText.includes(option.toLowerCase()),
        );

        if (matchedOption) {
          onChange(field.name, matchedOption);
        }
      }
    }
  }, [
    field.autoSelectFrom,
    sourceValue,
    field.options,
    field.name,
    hasValue,
    onChange,
  ]);

  const handleChangeDropdown = (newValue: string | string[]) => {
    onChange(field.name, newValue);
  };

  const onPressDropdown = () => {
    navigate.navigate("PickerScreen", {
      options: field.options || [],
      onValueChange: handleChangeDropdown,
      disableSearch: false,
      multiselect: field.multiselect,
      allowCustomInput: field.allowCustomInput,
    });
  };

  const renderChip = (item: string, index: number) => (
    <Chip
      key={index}
      onClose={() => {
        const newValue = value.filter(
          (selectedItem: string) => selectedItem !== item,
        );
        onChange(field.name, newValue);
      }}
    >
      {t(item)}
    </Chip>
  );

  return (
    <PickerFieldWrapper
      field={field}
      value={value}
      onChange={onChange}
      onPress={onPressDropdown}
      errors={errors}
      displayValue={t(value)}
      renderChip={field.multiselect ? renderChip : undefined}
    />
  );
};
